// The agent's OWN pipeline and performance (2026-08-11).
//
// Replaces the two company-wide sections on their dashboard. Both numbers come
// from GET /users/me/agent-dashboard, which is already scoped by the JWT — so
// there is no second filtering mechanism here, and nothing to get wrong.
//
// "Confirmed" means the agent clicked "✓ Confirmer la commande". Reaching a
// later status by dropdown or automation has never counted, and must not start
// counting here.
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/api';
import { TONE } from './dashTone';
import { useT } from '@/lib/i18n';

const GOLD = '#C9A24A';

/** Pipeline stages in the order work actually moves through them. */
// `hue`: a series colour for the 26 px figure and its bar (≥ 3:1 on a night card).
const STAGES: Array<{ keys: string[]; label: string; hue: string }> = [
  { keys: ['pending-album'], label: 'En attente album', hue: '#9CA3AF' },
  { keys: ['album-submitted'], label: 'À valider', hue: '#F59E0B' },
  { keys: ['ready-to-print', 'in-production', 'printed'], label: 'En production', hue: '#8B5CF6' },
  { keys: ['ready-to-deliver', 'shipped'], label: 'Expédié', hue: '#3B82F6' },
  { keys: ['delivered'], label: 'Livré', hue: '#10B981' },
];

export function AgentOwnStats() {
  const { t } = useT();
  // Same key as AgentEarningsCard — React Query serves both from one request.
  const { data, isLoading } = useQuery({
    queryKey: ['my-agent-dashboard'],
    queryFn: () => usersApi.myAgentDashboard(),
    staleTime: 60_000,
  });

  const pipeline = data?.pipeline ?? {};
  const counts = STAGES.map((s) => s.keys.reduce((n, k) => n + (pipeline[k] ?? 0), 0));
  const total = counts.reduce((a, b) => a + b, 0);
  const conv = data?.conversion;
  const avgMin = data?.avg_creation_to_confirm_minutes ?? null;

  const avgLabel = avgMin == null ? '—'
    : avgMin < 60 ? t('{n} min', { n: avgMin })
    : t('{n} h', { n: Math.round(avgMin / 60) });

  return (
    <>
      <div className="flex items-center gap-3 mb-3 mt-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: GOLD }}>
          {t('Mes commandes confirmées')}
        </span>
        <span className="h-px flex-1" style={{ background: 'rgba(201,162,74,.25)' }} />
      </div>

      <div
        className="mb-7 bg-white rounded-xl border px-5 py-4"
        style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
      >
        {total === 0 && !isLoading ? (
          <p className="text-sm text-gray-500">
            {t('Vous n’avez pas encore confirmé de commande.')}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {STAGES.map((s, i) => (
              <div key={s.label}>
                <p className="text-[10.5px] font-medium text-gray-400 uppercase tracking-wider mb-1">
                  {t(s.label)}
                </p>
                <p
                  className="text-[26px] leading-none tabular-nums"
                  style={{ color: s.hue, fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}
                >
                  {isLoading ? '…' : counts[i]}
                </p>
                <div className="mt-2 h-1 rounded-full" style={{ background: 'var(--color-adm-surface-3)' }}>
                  <div
                    className="h-1 rounded-full transition-all"
                    style={{
                      width: total > 0 ? `${Math.round((counts[i] / total) * 100)}%` : '0%',
                      background: s.hue,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: GOLD }}>
          {t('Ma performance')}
        </span>
        <span className="h-px flex-1" style={{ background: 'rgba(201,162,74,.25)' }} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-7">
        <Kpi
          label={t('Taux de livraison')}
          value={conv?.rate_pct == null ? '—' : `${conv.rate_pct}%`}
          sub={conv ? t('{d} livrées · {c} annulées', { d: conv.delivered, c: conv.cancelled }) : ''}
          ink="text-status-done"
          loading={isLoading}
        />
        <Kpi
          label={t('Délai moyen de confirmation')}
          value={isLoading ? '…' : avgLabel}
          sub={t('De la commande à votre clic')}
          ink={TONE.info.ink}
          loading={isLoading}
        />
        <Kpi
          label={t('Commandes confirmées')}
          value={isLoading ? '…' : String(data?.confirmed_count ?? 0)}
          sub={t('Depuis le début')}
          ink={TONE.gold.ink}
          loading={isLoading}
        />
      </div>
    </>
  );
}

function Kpi({ label, value, sub, ink, loading }: {
  /** the figure's colour, light + night (dashTone.ts) */
  label: string; value: string; sub: string; ink: string; loading: boolean;
}) {
  return (
    <div
      className="bg-white rounded-xl border px-5 py-4"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
    >
      <p className="text-[10.5px] font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p
        className={`text-[26px] leading-none ${ink}`}
        style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}
      >
        {loading ? '…' : value}
      </p>
      {sub && <p className="text-[11px] text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}
